import { createStore } from '../core/state/store';

/**
 * Background music.
 *
 * There was no audio of any kind in this app before — no element, no volume,
 * no mute. The three things that make browser background music behave are all
 * handled here so no screen has to think about them:
 *
 *  1. **Autoplay is blocked until the user interacts.** Browsers reject
 *     `play()` on a page nobody has touched. Rather than retrying in a loop or
 *     muting to sneak past the policy, the first real gesture arms playback;
 *     until then the mute button honestly shows "off".
 *  2. **The preference persists** in localStorage, which throws in private
 *     browsing — every access is guarded, and a blocked store degrades to
 *     "unmuted this session" rather than breaking the control.
 *  3. **One element, created lazily.** It is never constructed when the track
 *     is missing or the player has muted and never unmuted, so a build without
 *     the asset costs nothing.
 */

const STORAGE_KEY = 'dunes:audio';
const TRACK_OGG = '/audio/theme.ogg';
const TRACK_MP3 = '/audio/theme.mp3';

/** Music sits well under the UI; this is a bed, not a focus. */
const DEFAULT_VOLUME = 0.35;
const DEFAULT_SFX_VOLUME = 0.35;

export interface AudioState {
  /** What the player asked for. */
  muted: boolean;
  /** Whether a track is actually sounding right now — false while autoplay is still blocked. */
  playing: boolean;
  volume: number;
  sfxVolume: number;
}

function readStored(): { muted: boolean; volume: number; sfxVolume: number } {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { muted: false, volume: DEFAULT_VOLUME, sfxVolume: DEFAULT_SFX_VOLUME };
    const parsed = JSON.parse(raw) as Partial<AudioState>;
    return {
      muted: parsed.muted === true,
      volume: typeof parsed.volume === 'number' ? Math.min(Math.max(parsed.volume, 0), 1) : DEFAULT_VOLUME,
      sfxVolume: typeof parsed.sfxVolume === 'number' ? Math.min(Math.max(parsed.sfxVolume, 0), 1) : DEFAULT_SFX_VOLUME,
    };
  } catch {
    return { muted: false, volume: DEFAULT_VOLUME, sfxVolume: DEFAULT_SFX_VOLUME };
  }
}

function persist(state: AudioState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ muted: state.muted, volume: state.volume, sfxVolume: state.sfxVolume }));
  } catch {
    // Non-fatal: the control still works for this session.
  }
}

const stored = readStored();
export const audioStore = createStore<AudioState>({ ...stored, playing: false });

let element: HTMLAudioElement | null = null;
let armed = false;

function ensureElement(): HTMLAudioElement {
  if (element) return element;
  const audio = document.createElement('audio');
  audio.loop = true;
  audio.preload = 'none';
  audio.volume = audioStore.get().volume;

  // Opus first (smaller), MP3 as the universal fallback — Safari has no Opus
  // in an Ogg container.
  const ogg = document.createElement('source');
  ogg.src = TRACK_OGG;
  ogg.type = 'audio/ogg; codecs=opus';
  const mp3 = document.createElement('source');
  mp3.src = TRACK_MP3;
  mp3.type = 'audio/mpeg';
  audio.append(ogg, mp3);

  // A missing track must not surface as an error to the player; the mute
  // button simply never lights up.
  audio.addEventListener('error', () => audioStore.set({ ...audioStore.get(), playing: false }));
  audio.addEventListener('playing', () => audioStore.set({ ...audioStore.get(), playing: true }));
  audio.addEventListener('pause', () => audioStore.set({ ...audioStore.get(), playing: false }));

  element = audio;
  return audio;
}

async function startIfWanted(): Promise<void> {
  const state = audioStore.get();
  if (state.muted || !armed) return;
  const audio = ensureElement();
  audio.volume = state.volume;
  try {
    await audio.play();
  } catch {
    // Autoplay still refused, or no track. Leave `playing` false — the control
    // reflects reality rather than claiming success.
  }
}

/**
 * Arms playback on the first real user gesture.
 *
 * Called once at boot. Browsers reject `play()` before any interaction, so the
 * listeners below are the earliest honest moment to start.
 */
export function initAudio(): () => void {
  const onFirstGesture = () => {
    armed = true;
    void startIfWanted();
  };
  const opts = { once: true, passive: true } as const;
  window.addEventListener('pointerdown', onFirstGesture, opts);
  window.addEventListener('keydown', onFirstGesture, opts);

  return () => {
    window.removeEventListener('pointerdown', onFirstGesture);
    window.removeEventListener('keydown', onFirstGesture);
    element?.pause();
  };
}

export function setMuted(muted: boolean): void {
  const next = { ...audioStore.get(), muted };
  audioStore.set(next);
  persist(next);
  if (muted) element?.pause();
  else void startIfWanted();
}

export function toggleMuted(): void {
  setMuted(!audioStore.get().muted);
}

export function setVolume(volume: number): void {
  const clamped = Math.min(Math.max(volume, 0), 1);
  const next = { ...audioStore.get(), volume: clamped };
  audioStore.set(next);
  persist(next);
  if (element) element.volume = clamped;
}

/**
 * The gain SFX should actually play at. The mute toggle is a single control
 * over ALL sound, so it has to zero this too — it used to only pause the
 * music element, leaving every click and hover audible on a muted game.
 *
 * Read per play rather than pushed onto the pooled elements, so toggling mute
 * takes effect on the very next sound with nothing to keep in sync.
 */
export function effectiveSfxVolume(): number {
  const { muted, sfxVolume } = audioStore.get();
  return muted ? 0 : sfxVolume;
}

export function setSfxVolume(volume: number): void {
  const clamped = Math.min(Math.max(volume, 0), 1);
  const next = { ...audioStore.get(), sfxVolume: clamped };
  audioStore.set(next);
  persist(next);
}

/** Test seam: drops the element and the armed flag so each case starts clean. */
export function __resetAudioForTests(): void {
  element?.pause();
  element = null;
  armed = false;
  audioStore.set({ ...readStored(), playing: false });
}
